import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, firstValueFrom } from 'rxjs';

import { ApiService } from '../../../../../services/api.service';
import { ContadorService } from '../../../../../services/contador/contador.service';
import { DespesaAcessoriaService } from '../../../../../services/contador/despesa-acessoria.service';
import { ContaService } from '../../../../../services/conta/conta.service';
import { TipoDespesaService } from '../../../../../services/enums/tipo-despesa.service';
import { NotaService } from '../../../../../services/financeiro/notas.service';
import { CotacaoService } from '../../../../../services/cotacao/cotacao.service';
import { UsuarioService } from '../../../../../services/usuario/usuario.service';
import { ContratoContadorConsultaDTO, MesReferencia } from '../../../../../models/contador/contador.model';
import { ContratoInfoVM } from '../../../../../models/contador/contrato-info.model';
import {
  AdicionarParcelasDTO,
  CancelamentoParcelasLoteResultadoDTO,
  EstadoClienteParcela,
  EstadoContadorParcela,
  ParcelaContratoConsultaDTO,
  ParcelaContratoFiltroDTO,
  ParcelaLoteIgnoradaDTO,
  PrepararFaturamentoDTO,
  VincularFaturamentoResultadoDTO,
} from '../../../../../models/contador/parcela-contrato.model';
import { PeriodoFiltro } from '../../../../../models/dashboard/periodo-filtro.model';
import { toLocalISOString } from '../../../../../shared/utils/date-utils';
import {
  DespesaAcessoriaConsultaDTO,
  DespesaAcessoriaDTO,
  DespesaAcessoriaFiltroDTO,
  DespesaAcessoriaFormDTO,
  FiltroCobrancaDespesa,
  MesReferenciaDespesa,
  StatusDespesaAcessoria,
} from '../../../../../models/contador/despesa-acessoria.model';
import { ContaDTO, MoedaBackend } from '../../../../../models/gerenciar-contas/conta.model';
import { TipoDespesaBackendDTO } from '../../../../../models/gerenciar-contas/despesa.model';
import { TipoDespesaDTO } from '../../../../../models/tipo-despesa.model';
import { TipoDeDespesa, TIPOS_DESPESA_NAO_EXIBIR } from '../../../../../enums/TipoDespesa.enum';
import { Moeda, MOEDA_CONFIG, MoedaConfig } from '../../../../../enums/moeda.enum';

/** Opção do dropdown de forma de pagamento (conta do contador). */
interface FormaPagamentoOption {
  label: string;
  value: number;
}

/**
 * Aba "Contrato e Despesas" da tela Operar Cliente (Área do Contador).
 *
 * Exibe o CONTRATO ATIVO do contador com o cliente selecionado e oferece o CRUD
 * completo das despesas acessórias do contrato (adicionar, listar, editar,
 * excluir). O contrato é resolvido buscando os contratos do contador
 * (GET /contador/contratos) e filtrando por `status === 'ATIVO'` e
 * `configuracaoEmpresaId === clienteEmpresaId`. O CRUD usa os endpoints
 * `/contador/contratos/{contratoId}/despesas` (contador-only, resolvidos por
 * contador autenticado + contratoId).
 *
 * `clienteEmpresaId` (empresa do cliente) muda quando o contador troca de
 * cliente — dispara a recarga do contrato e das despesas.
 */
@Component({
  selector: 'app-operar-despesa-acessoria',
  templateUrl: './despesa-acessoria.component.html',
  styleUrls: ['./despesa-acessoria.component.scss'],
})
export class OperarDespesaAcessoriaComponent implements OnInit, OnChanges, OnDestroy {
  /** Id da empresa do cliente — muda quando o contador troca de cliente. */
  @Input() clienteEmpresaId?: number | null;

  /**
   * Recorte exibido: 'contrato' mostra so o card do contrato ativo; 'gastos'
   * mostra so o CRUD de despesas acessorias; 'completo' mostra tudo (default,
   * preserva usos legados). Permite dividir a antiga aba "Contrato e Despesas"
   * em duas abas no Operar Cliente reusando o mesmo componente.
   */
  @Input() modo: 'completo' | 'contrato' | 'gastos' = 'completo';

  // Contrato ativo
  carregandoContrato = false;
  erroContrato = false;
  contrato: ContratoContadorConsultaDTO | null = null;
  contratoId: number | null = null;

  /** View-model do card compartilhado do contrato (app-contrato-info-card). */
  contratoInfoVM: ContratoInfoVM | null = null;

  /** Valor atual do dropdown inline "Mês de referência" no card do contrato. */
  contratoMesReferencia: MesReferencia | null = null;
  /** Loading do PATCH de mês de referência (desabilita o dropdown enquanto salva). */
  salvandoMesReferencia = false;

  // Estado do diálogo de cancelamento do CONTRATO (data efetiva selecionável).
  /** Controla a visibilidade do diálogo de cancelamento do contrato. */
  cancelarContratoDialogVisivel = false;
  /** Data de cancelamento escolhida (default: hoje). Enviada como corte ao backend. */
  cancelarContratoData: Date | null = null;
  /**
   * Piso do calendário de cancelamento: HOJE. Data retroativa é proibida — o corte é
   * aplicado como `vencimento >= data`, então uma data no passado ampliaria o
   * cancelamento e alcançaria títulos e parcelas já VENCIDOS e ainda em aberto,
   * apagando dívida madura dos dois lados. O backend também recusa (422).
   */
  dataMinimaCancelamento = new Date();
  /** Loading do PATCH de cancelamento (desabilita os botões do diálogo). */
  cancelandoContrato = false;

  /** Loading dos PATCH inline de valor mensal / dia / parcelas por mês do contrato. */
  salvandoValorMensal = false;
  salvandoDiaCobranca = false;
  salvandoParcelasPorMes = false;

  /** Diálogo de confirmação da edição inline do contrato. */
  confirmarEdicaoVisivel = false;
  /** Campo em edição e o novo valor pendente de confirmação. */
  edicaoCampo: 'valor' | 'dia' | 'parcelasPorMes' | null = null;
  edicaoValorPendente: number | null = null;

  // Estado do diálogo wave de confirmação da troca do mês de referência.
  /** Controla a visibilidade do diálogo wave de confirmação do mês de referência. */
  mesReferenciaDialogVisivel = false;
  /** Novo valor de mês de referência aguardando confirmação (usado no subtítulo). */
  mesReferenciaNovo: MesReferencia | null = null;
  /** Valor a restaurar no dropdown se o usuário cancelar/fechar sem confirmar. */
  private mesReferenciaAnterior: MesReferencia = 'MES_ATUAL';
  /** Marca a confirmação para NÃO reverter o dropdown no onHide do diálogo. */
  private mesReferenciaConfirmado = false;

  /** Config de formatação da moeda do contrato (símbolo/casas/locale). */
  moedaConfig: MoedaConfig = MOEDA_CONFIG[Moeda.GUARANI];
  /** Casas decimais do p-inputNumber conforme a moeda do contrato. */
  fracDigits = 0;
  /** Código da moeda do contrato — usado para derivar o glifo do prefixo. */
  moedaCodigo: Moeda = Moeda.GUARANI;

  /**
   * Glifos de moeda para o prefixo do campo Valor. Iguais aos usados em
   * produto-selecionar/nova-receita (consistência no projeto). Note que
   * moedaConfig.symbol é o código ISO (PYG/BRL/...), por isso o mapa próprio.
   */
  private static readonly SIMBOLOS_MOEDA: Record<Moeda, string> = {
    [Moeda.GUARANI]: 'Gs',
    [Moeda.REAL]: 'R$',
    [Moeda.DOLAR]: '$',
    [Moeda.PESO]: '$',
  };

  /** Prefixo (glifo + espaço) exibido dentro do p-inputNumber do campo Valor. */
  get prefixoMoeda(): string {
    return `${OperarDespesaAcessoriaComponent.SIMBOLOS_MOEDA[this.moedaCodigo] ?? ''} `;
  }

  // Despesas (paginacao + ordenacao SERVER-SIDE; padrao da tela de Indicacoes/cupons).
  /** Conteudo da PAGINA atual (Page.content), vinculado direto a p-table. */
  despesas: DespesaAcessoriaConsultaDTO[] = [];
  carregandoDespesas = false;
  erroDespesas = false;

  // Paginacao server-side (paginador separado no rodape + "itens por pagina").
  first = 0;
  rows = 10;
  /** Total de registros no backend (dirige o p-paginator). */
  totalRecords = 0;
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /**
   * Ordenacao por cabecalho enviada ao backend (campo do Page/Spring). Inicia no
   * default do backend (`dataPagamento` desc) para o indicador visual da coluna
   * bater com a ordem carregada. Campos aceitos: `valor`, `dataPagamento`,
   * `tipoDespesa`, `status` (a coluna Tipo envia `tipoDespesa`, nao `tipo`).
   */
  sortField: string | null = 'dataPagamento';
  /** Direcao: 1 = asc, -1 = desc, 0 = sem ordenacao (cai no default do backend). */
  sortOrder = -1;

  // Filtros da lista de gastos (server-side, mesmo padrao das parcelas).
  /** Intervalo (ISO yyyy-MM-dd) do filtro de periodo por dataPagamento; undefined = sem filtro. */
  despesasDe: string | undefined = undefined;
  despesasAte: string | undefined = undefined;
  /** Tipo de gasto filtrado (nome do enum TipoDespesa); null = todos. */
  despesasTipo: string | null = null;
  /** Situacao filtrada; null = todas. */
  despesasStatus: StatusDespesaAcessoria | null = null;
  /** Cobranca filtrada; null = todas. */
  despesasCobranca: FiltroCobrancaDespesa | null = null;

  /** Opcoes dos dropdowns de filtro; rotulos no idioma atual. Em `despesasTipoOptions`
   *  o `cor`/`icone` vem do backend e so existe nos tipos reais (a opcao "Todos"
   *  fica sem circulo colorido). */
  despesasTipoOptions: {
    label: string;
    value: string | null;
    cor?: string | null;
    icone?: string | null;
  }[] = [];
  despesasStatusOptions: { label: string; value: StatusDespesaAcessoria | null }[] = [];
  despesasCobrancaOptions: { label: string; value: FiltroCobrancaDespesa | null }[] = [];

  // ===========================================================================
  // Parcelas do contrato (grid da aba "Contrato"; paginacao + ordenacao
  // SERVER-SIDE, mesmo padrao dos Gastos lancados). Estado SEPARADO do grid de
  // despesas porque no modo 'completo' os dois grids coexistem.
  // ===========================================================================
  /** Conteudo da PAGINA atual (Page.content) das parcelas, vinculado a p-table. */
  parcelas: ParcelaContratoConsultaDTO[] = [];
  carregandoParcelas = false;
  erroParcelas = false;

  // Paginacao server-side (paginador separado no rodape + "itens por pagina").
  parcelasFirst = 0;
  parcelasRows = 10;
  /** Total de registros da QUERY atual (com filtros) — dirige o p-paginator. */
  parcelasTotalRecords = 0;

  /**
   * Total de parcelas do CONTRATO (todas, sem filtro de situacao/periodo) — usado
   * como denominador da coluna "Nº" (x/total). Obtido por uma chamada leve sem
   * filtro (page=0, size=1) ao carregar o contrato e apos adicionar parcelas.
   * `null` enquanto nao carregou (a coluna cai no fallback so-numero).
   */
  contratoTotalParcelas: number | null = null;

  /**
   * Ordenacao por cabecalho das parcelas (campo do Page/Spring). Inicia no
   * default do backend (`numeroParcela` asc) para o indicador visual bater com a
   * ordem carregada. Campos aceitos: `numeroParcela`, `dataReferencia`,
   * `dataVencimento`, `valorParcela`, `saldo`, `estadoCliente`, `estadoContador`.
   */
  parcelasSortField: string | null = 'numeroParcela';
  /** Direcao: 1 = asc, -1 = desc, 0 = sem ordenacao (cai no default do backend). */
  parcelasSortOrder = 1;

  /**
   * Selecao multipla de parcelas (mesmo padrao da tabela de comissoes): clique
   * na linha alterna, shift seleciona intervalo, checkbox do cabecalho marca as
   * selecionaveis da pagina. Canceladas (qualquer um dos lados) ficam fora.
   */
  parcelasSelecionadas: ParcelaContratoConsultaDTO[] = [];

  /** Guard de selecao usado pelo p-table ([rowSelectable] recebe {data}). */
  isParcelaSelecionavel = (event: { data: ParcelaContratoConsultaDTO }): boolean =>
    this.parcelaSelecionavel(event.data);

  /** Parcela cancelada em qualquer um dos lados nao e selecionavel. */
  parcelaSelecionavel(parcela: ParcelaContratoConsultaDTO): boolean {
    return parcela.estadoCliente !== 'CANCELADA' && parcela.estadoContador !== 'CANCELADA';
  }

  /**
   * Elegibilidade para EXCLUIR (cancelar) — replica exatamente o criterio do
   * backend (`ParcelaContratoService.cancelarParcelasEmLote`): so cancela quando
   * os DOIS lados estao PENDENTES. Faturada/recebida/paga nao pode ser excluida.
   */
  parcelaExcluivel(parcela: ParcelaContratoConsultaDTO): boolean {
    return parcela.estadoCliente === 'PENDENTE' && parcela.estadoContador === 'PENDENTE';
  }

  // Filtros das parcelas (periodo por mes de referencia + situacao do contador).
  /** Intervalo (ISO yyyy-MM-dd) do filtro de periodo; undefined = sem filtro. */
  parcelasDe: string | undefined = undefined;
  parcelasAte: string | undefined = undefined;
  /** Situacao (estado do contador) filtrada; null = todas. */
  parcelasSituacao: EstadoContadorParcela | null = null;
  /** Opcoes do dropdown de situacao ({ label, value }); rotulos no idioma atual. */
  parcelasSituacaoOptions: { label: string; value: EstadoContadorParcela | null }[] = [];

  // Cancelamento em lote (excluir parcelas) — acao destrutiva com confirmacao.
  /** Loading do fluxo de exclusao em lote (desabilita o botao). */
  excluindoParcelas = false;
  /** Controla a visibilidade do dialog "Excluir parcelas" (padrao dialog-wave). */
  excluirParcelasDialogVisivel = false;

  // Adicionar parcelas (dialog com quantidade + periodicidade).
  /** Controla a visibilidade do dialog de "Adicionar parcelas". */
  adicionarParcelasDialogVisivel = false;
  /** Campo "quantidade" do dialog (>= 1). */
  novaQuantidadeParcelas: number | null = 1;
  /** Campo "periodicidade" do dialog (>= 1). */
  novaPeriodicidadeParcelas: number | null = 1;
  /** Data do 1o vencimento (OPCIONAL). Vazio => comportamento default do backend. */
  novaDataPrimeiroVencimento: Date | null = null;
  /** Marca submit para exibir erros de validacao inline. */
  adicionarParcelasSubmetido = false;
  /** Loading do botao de confirmacao do dialog de adicionar parcelas. */
  adicionandoParcelas = false;

  // Faturar parcelas (a venda e criada na EMPRESA DO CONTADOR — sem contexto de
  // cliente). Fluxo: preparar (backend) -> montar+criar a nota -> vincular (backend).
  /** Controla a visibilidade do dialog de faturamento. */
  faturarDialogVisivel = false;
  /** Dados retornados pelo "preparar" (cliente, item, valor, periodo, vencimento). */
  prepFaturamento: PrepararFaturamentoDTO | null = null;
  /** Forma de pagamento escolhida no dialog: 'contado' (a vista) ou 'prazo' (1 titulo). */
  formaPagamentoFaturar: 'contado' | 'prazo' = 'prazo';
  /** Loading do fluxo (preparar -> nota -> vincular). */
  processandoFaturamento = false;
  /** Opcoes do seletor de forma de pagamento ({ label, value }); reconstruidas no idioma. */
  formaPagamentoFaturarOptions: { label: string; value: 'contado' | 'prazo' }[] = [];

  /** Juros: o contador marca "Adicionar juros" e informa o percentual mensal (juros simples). */
  adicionarJuros = false;
  /** Percentual mensal de juros simples informado no dialog (aplicado as parcelas atrasadas). */
  percentualJuros: number | null = null;
  /** Loading do recalculo do juros (re-preparar com o percentual para atualizar o preview). */
  recalculandoJuros = false;

  /**
   * Vendedor da venda: a PESSOA do usuario logado (o contador). A nota e criada
   * na empresa do CONTADOR (POST /nota sem contexto de cliente), por isso o
   * vendedor e o proprio usuario autenticado — nunca alguem da empresa do
   * cliente. O backend exige `vendedor_id` em notas de VENDA.
   */
  vendedorPessoaId: number | null = null;
  /** Nome do vendedor (pessoa do usuario logado) exibido no resumo do dialog. */
  vendedorNome = '';

  /**
   * So permite faturar quando ha selecao e TODAS as parcelas selecionadas estao
   * PENDENTES no lado do contador (as demais nao sao faturaveis). O backend
   * revalida no "preparar" (422), mas o gate evita a ida desnecessaria.
   */
  get parcelasFaturaveis(): boolean {
    return (
      this.parcelasSelecionadas.length > 0 &&
      this.parcelasSelecionadas.every((p) => p.estadoContador === 'PENDENTE')
    );
  }

  /**
   * So permite excluir quando ha selecao e TODAS as parcelas selecionadas sao
   * excluiveis (PENDENTE nos dois lados). Evita a impressao de que uma parcela
   * ja faturada/paga seria removida — o backend a devolveria em `ignoradas`.
   */
  get parcelasExcluiveis(): boolean {
    return (
      this.parcelasSelecionadas.length > 0 &&
      this.parcelasSelecionadas.every((p) => this.parcelaExcluivel(p))
    );
  }

  // Formulário (adicionar/editar)
  form: DespesaAcessoriaFormDTO = this.formVazio();
  submetido = false;
  salvando = false;
  /** Id da despesa em edição; null = criando uma nova. */
  editandoId: number | null = null;
  /**
   * A despesa em edição já está ATIVA: a alteração mexe nos títulos dos dois lados, então a tela
   * avisa e trava a forma de cobrança (`especial`) — mudá-la criaria/removeria lançamentos e o
   * backend recusa com 422.
   */
  editandoAtiva = false;
  /**
   * A despesa em edição NÃO ajusta o caixa sozinho (anterior à V1.106, sem vínculo com o
   * movimento) — só aí a tela pede o ajuste manual. A especial é tratada à parte, no template.
   */
  editandoAtivaSemAjusteDeCaixa = false;

  // Exclusão (diálogo de confirmação customizado — padrão dialog-wave do projeto).
  /** Controla a visibilidade do diálogo de confirmação de exclusão. */
  excluirDialogVisivel = false;
  /** Despesa selecionada para exclusão (dirige o subtítulo e a ação do diálogo). */
  despesaParaExcluir: DespesaAcessoriaConsultaDTO | null = null;
  /** Loading do botão de confirmação enquanto a exclusão é processada. */
  excluindo = false;

  /**
   * Aba interna ativa: 'form' (adicionar/editar gasto) ou 'lista' (gastos
   * lançados). Segmentado com p-selectButton (padrao do projeto para
   * sub-navegacao; nao aninhamos p-tabView). Inicia na listagem.
   */
  abaAtiva: 'form' | 'lista' = 'lista';

  /** Opcoes do seletor de aba ({ label, value }); rotulo reconstruido no idioma
   *  e conforme o modo (novo/editar) do formulario. */
  abaOpcoes: { label: string; value: 'form' | 'lista' }[] = [];

  // Tipo de despesa (dropdown)
  tipoDespesaOptions: TipoDespesaDTO[] = [];
  /** Mapa enum -> nome localizado, para exibir o rótulo na listagem. */
  private tipoDespesaMap = new Map<string, string>();

  // Forma de pagamento (conta do contador, dropdown) — só usada quando NÃO especial.
  formaPagamentoOptions: FormaPagamentoOption[] = [];

  /**
   * Opções do dropdown "Mês de referência" ({ label, value }); rótulos
   * reconstruídos ao trocar de idioma. A ordem segue a competência do gasto
   * (anterior -> atual -> próximo). O default do formulário é `MES_ATUAL`.
   */
  mesReferenciaOptions: { label: string; value: MesReferenciaDespesa }[] = [];

  /** Valores do enum "Mês de referência" na ordem de exibição do dropdown. */
  private static readonly MESES_REFERENCIA: MesReferenciaDespesa[] = [
    'MES_ANTERIOR',
    'MES_ATUAL',
    'PROXIMO_MES',
  ];

  /**
   * Tipo de despesa padrão para "gasto especial", lido da Configuração Geral
   * (enum TipoDespesa como string). Usado para pré-selecionar o "Tipo de gasto"
   * quando o usuário marca a opção "Gasto especial". Default do backend:
   * RETENCOES_DE_IVA.
   */
  private tipoDespesaEspecialPadrao: string | null = null;

  /** Subscription da troca de idioma (cancelada em ngOnDestroy). */
  private langChangeSub?: Subscription;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private contadorService: ContadorService,
    private despesaService: DespesaAcessoriaService,
    private contaService: ContaService,
    private tipoDespesaService: TipoDespesaService,
    private messageService: MessageService,
    private notaService: NotaService,
    private cotacaoService: CotacaoService,
    private usuarioService: UsuarioService,
    private translate: TranslateService,
  ) {}

  // ===========================================================================
  // Ciclo de vida
  // ===========================================================================

  ngOnInit(): void {
    this.buildAbaOpcoes();
    this.buildMesReferenciaOptions();
    this.buildParcelasSituacaoOptions();
    this.buildDespesasStatusOptions();
    this.buildDespesasCobrancaOptions();
    this.buildDespesasTipoOptions();
    this.buildFormaPagamentoFaturarOptions();
    this.carregarTiposDespesa();
    this.carregarFormasPagamento();
    this.carregarTipoDespesaEspecialPadrao();
    this.carregarVendedor();
    this.carregarContrato();
    // Recarrega os tipos e os rotulos das abas ao trocar de idioma
    // (o `nome` do tipo vem localizado do backend).
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.carregarTiposDespesa();
      this.buildAbaOpcoes();
      this.buildMesReferenciaOptions();
      this.buildParcelasSituacaoOptions();
      this.buildDespesasStatusOptions();
      this.buildDespesasCobrancaOptions();
      this.buildFormaPagamentoFaturarOptions();
    });
  }

  /** Opcoes do seletor de forma de pagamento do faturamento (rotulos no idioma). */
  private buildFormaPagamentoFaturarOptions(): void {
    this.formaPagamentoFaturarOptions = [
      {
        value: 'prazo',
        label: this.translate.instant('contador_operar.despesa_acessoria.parcelas.faturar_prazo'),
      },
      {
        value: 'contado',
        label: this.translate.instant('contador_operar.despesa_acessoria.parcelas.faturar_contado'),
      },
    ];
  }

  /** Situacoes possiveis do gasto — ordem exibida no filtro. */
  private static readonly SITUACOES_DESPESA: StatusDespesaAcessoria[] = [
    'PENDENTE',
    'ATIVA',
    'RECUSADA',
    'CANCELADA',
  ];

  /** Valores possiveis do filtro de cobranca — ordem exibida no dropdown. */
  private static readonly COBRANCAS_DESPESA: FiltroCobrancaDespesa[] = ['A_COBRAR', 'COBRADO'];

  /**
   * Monta as opcoes do filtro de SITUACAO do gasto. Primeira opcao ("Todas") = null.
   * Reconstruida ao trocar de idioma.
   */
  private buildDespesasStatusOptions(): void {
    this.despesasStatusOptions = [
      {
        value: null,
        label: this.translate.instant('contador_operar.despesa_acessoria.filtros.situacao_todas'),
      },
      ...OperarDespesaAcessoriaComponent.SITUACOES_DESPESA.map((s) => ({
        value: s,
        label: this.statusLabel(s),
      })),
    ];
  }

  /**
   * Monta as opcoes do filtro de COBRANCA. Primeira opcao ("Todas") = null; os
   * rotulos reusam os da tag da coluna (A cobrar / Cobrado).
   */
  private buildDespesasCobrancaOptions(): void {
    this.despesasCobrancaOptions = [
      {
        value: null,
        label: this.translate.instant('contador_operar.despesa_acessoria.filtros.cobranca_todas'),
      },
      ...OperarDespesaAcessoriaComponent.COBRANCAS_DESPESA.map((c) => ({
        value: c,
        label: this.translate.instant(
          c === 'COBRADO'
            ? 'contador_operar.despesa_acessoria.pagamento.pago'
            : 'contador_operar.despesa_acessoria.pagamento.a_receber',
        ),
      })),
    ];
  }

  /**
   * Monta as opcoes do filtro de TIPO de gasto a partir dos tipos ja carregados
   * para o formulario (rotulos localizados vindos do backend). Primeira opcao
   * ("Todos") = null. Chamada sempre que os tipos sao (re)carregados.
   */
  private buildDespesasTipoOptions(): void {
    this.despesasTipoOptions = [
      {
        value: null,
        label: this.translate.instant('contador_operar.despesa_acessoria.filtros.tipo_todos'),
      },
      // Mantem cor/icone do backend para o dropdown desenhar o mesmo circulo
      // colorido usado no formulario e na coluna "Tipo".
      ...this.tipoDespesaOptions.map((t) => ({
        value: t.enum,
        label: t.nome,
        cor: t.cor,
        icone: t.icone,
      })),
    ];
  }

  /** Estados possiveis do lado a receber (contador) — ordem exibida no dropdown. */
  private static readonly SITUACOES_PARCELA: EstadoContadorParcela[] = [
    'PENDENTE',
    'FATURADA',
    'RECEBIDA',
    'CANCELADA',
  ];

  /**
   * Monta as opcoes do dropdown de situacao (estado do contador) com rotulos
   * traduzidos. A primeira opcao ("Todas") = null. Reconstruida ao trocar idioma.
   */
  private buildParcelasSituacaoOptions(): void {
    this.parcelasSituacaoOptions = [
      {
        value: null,
        label: this.translate.instant('contador_operar.despesa_acessoria.parcelas.filtro_situacao_todas'),
      },
      ...OperarDespesaAcessoriaComponent.SITUACOES_PARCELA.map((s) => ({
        value: s,
        label: this.estadoContadorLabel(s),
      })),
    ];
  }

  /**
   * Monta as opções do dropdown "Mês de referência" com os rótulos traduzidos
   * (reconstruídas ao trocar de idioma).
   */
  private buildMesReferenciaOptions(): void {
    this.mesReferenciaOptions = OperarDespesaAcessoriaComponent.MESES_REFERENCIA.map((mes) => ({
      label: this.mesReferenciaLabel(mes),
      value: mes,
    }));
  }

  /**
   * Normaliza o mês de referência vindo do backend para o valor do formulário.
   * Cai em `MES_ATUAL` quando ausente (itens legados sem o campo).
   */
  private extrairMesReferencia(mes: MesReferenciaDespesa | string | null | undefined): MesReferenciaDespesa {
    return OperarDespesaAcessoriaComponent.MESES_REFERENCIA.includes(mes as MesReferenciaDespesa)
      ? (mes as MesReferenciaDespesa)
      : 'MES_ATUAL';
  }

  /** Rótulo localizado do mês de referência (texto simples para form/listagem). */
  mesReferenciaLabel(mes: MesReferenciaDespesa | string | null | undefined): string {
    if (!mes) {
      return '-';
    }
    const chave = `contador_operar.despesa_acessoria.mes_referencia_valores.${mes.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? mes : traduzido;
  }

  /**
   * Monta as opcoes do seletor de aba. O rotulo da aba do formulario reflete o
   * modo atual (Novo gasto x Editar gasto), acompanhando o cabecalho do card.
   */
  private buildAbaOpcoes(): void {
    this.abaOpcoes = [
      {
        label: this.translate.instant(
          this.editando
            ? 'contador_operar.despesa_acessoria.aba_editar'
            : 'contador_operar.despesa_acessoria.aba_novo',
        ),
        value: 'form',
      },
      {
        label: this.translate.instant('contador_operar.despesa_acessoria.aba_lista'),
        value: 'lista',
      },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recarrega ao trocar de cliente (depois do primeiro load do ngOnInit).
    if (changes['clienteEmpresaId'] && !changes['clienteEmpresaId'].firstChange) {
      this.resetForm();
      this.carregarContrato();
    }
  }

  /**
   * Abre os titulos do cliente em contas a receber (a mesma ficha da lista de
   * Clientes). O id da pessoa vem da propria listagem: gasto cujo cliente ainda
   * nao virou pessoa — porque nao gerou titulo — nao tem para onde levar, e a
   * linha fica sem clique.
   */
  abrirTitulosDoCliente(despesa: DespesaAcessoriaConsultaDTO): void {
    const pessoaId = despesa?.pessoaId;
    if (!pessoaId) return;
    this.router.navigate(['/contas-a-receber/pessoa', pessoaId], {
      queryParams: {
        nome: this.contrato?.nomeEmpresa || '',
        tipo: 'ENTRADA',
        // O "voltar" da ficha reabre o Operar Cliente NESTE mesmo cliente.
        origem: 'contador-operar',
        clienteId: this.clienteEmpresaId ?? undefined,
      },
    });
  }

  temTitulosParaAbrir(despesa: DespesaAcessoriaConsultaDTO): boolean {
    return !!despesa?.pessoaId;
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  // ===========================================================================
  // Contrato ativo
  // ===========================================================================

  /** Busca o contrato ATIVO do contador com o cliente selecionado. */
  carregarContrato(): void {
    this.contrato = null;
    this.contratoId = null;
    this.despesas = [];
    this.parcelas = [];
    this.parcelasTotalRecords = 0;
    this.contratoTotalParcelas = null;
    this.parcelasFirst = 0;
    this.erroParcelas = false;

    if (this.clienteEmpresaId == null) {
      this.carregandoContrato = false;
      this.erroContrato = false;
      return;
    }

    this.carregandoContrato = true;
    this.erroContrato = false;

    // size grande + page 0: o contador tem poucos contratos; um único fetch basta.
    this.contadorService.listarContratos(0, 100).subscribe({
      next: (res) => {
        const lista = res.content || [];
        const ativo = lista.find(
          (c) =>
            c.status === 'ATIVO' &&
            c.configuracaoEmpresaId === this.clienteEmpresaId &&
            c.id != null,
        );
        if (ativo) {
          this.aplicarContrato(ativo);
          this.carregarDespesas();
          // Parcelas aparecem na aba "Contrato" (modo 'contrato'/'completo').
          if (this.modo !== 'gastos') {
            this.carregarParcelas();
            this.carregarTotalParcelasContrato();
          }
        }
        this.carregandoContrato = false;
      },
      error: () => {
        this.contrato = null;
        this.contratoId = null;
        this.erroContrato = true;
        this.carregandoContrato = false;
      },
    });
  }

  private aplicarContrato(contrato: ContratoContadorConsultaDTO): void {
    this.contrato = contrato;
    this.contratoId = contrato.id;
    // Espelha o mês de referência no dropdown inline do card (default MES_ATUAL).
    this.contratoMesReferencia = contrato.mesReferencia ?? 'MES_ATUAL';
    const codigo = (contrato.moeda?.enum as Moeda) ?? Moeda.GUARANI;
    this.moedaCodigo = codigo;
    this.moedaConfig = MOEDA_CONFIG[codigo] ?? MOEDA_CONFIG[Moeda.GUARANI];
    this.fracDigits = codigo === Moeda.GUARANI ? 0 : 2;
    // Mapeia o DTO -> view-model do card compartilhado (app-contrato-info-card).
    this.contratoInfoVM = {
      nome: contrato.nomeEmpresa,
      status: contrato.status,
      valorMensal: contrato.valorMensal,
      moedaNome: contrato.moeda?.nome ?? null,
      percentualDesconto: contrato.percentualDesconto,
      diaCobranca: contrato.diaCobranca,
      parcelasPorMes: contrato.parcelasPorMes ?? null,
      criadoEm: contrato.criadoEm,
      dataVencimento: contrato.dataVencimento,
      autonomiaContador: contrato.autonomiaContador,
    };
  }

  /**
   * Troca do mês de referência pelo dropdown inline do card. Como recalcula a
   * competência das parcelas futuras, abre um diálogo wave de confirmação antes
   * do PATCH. Fechar sem confirmar (Cancelar / ESC / clique fora) reverte o
   * dropdown para o valor atual do contrato (ver onMesReferenciaHide).
   */
  onMesReferenciaContratoChange(novo: MesReferencia): void {
    const atual = this.contrato?.mesReferencia ?? 'MES_ATUAL';
    if (this.salvandoMesReferencia || this.contratoId == null || novo === atual) {
      return;
    }
    this.mesReferenciaNovo = novo;
    this.mesReferenciaAnterior = atual;
    this.mesReferenciaConfirmado = false;
    this.mesReferenciaDialogVisivel = true;
  }

  /** Confirma a troca (botão do diálogo wave): fecha o diálogo e dispara o PATCH. */
  confirmarMesReferencia(): void {
    if (this.mesReferenciaNovo == null) {
      return;
    }
    const novo = this.mesReferenciaNovo;
    this.mesReferenciaConfirmado = true;
    this.mesReferenciaDialogVisivel = false;
    this.salvarMesReferencia(novo);
  }

  /** Fecha o diálogo (Cancelar). O revert do dropdown ocorre em onMesReferenciaHide. */
  fecharMesReferencia(): void {
    this.mesReferenciaDialogVisivel = false;
  }

  /**
   * Disparado ao fechar o diálogo por qualquer via (Cancelar / ESC / clique fora).
   * Se NÃO foi confirmado, reverte o dropdown ao valor vigente do contrato.
   */
  onMesReferenciaHide(): void {
    if (!this.mesReferenciaConfirmado) {
      this.contratoMesReferencia = this.mesReferenciaAnterior;
    }
    this.mesReferenciaNovo = null;
  }

  private salvarMesReferencia(mesReferencia: MesReferencia): void {
    if (this.contratoId == null) {
      return;
    }
    this.salvandoMesReferencia = true;
    this.contadorService.definirMesReferencia(this.contratoId, mesReferencia).subscribe({
      next: (contratoAtualizado) => {
        this.salvandoMesReferencia = false;
        // Atualiza o card + recarrega as parcelas (competência recalculada).
        this.aplicarContrato(contratoAtualizado);
        this.carregarParcelas(0);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('contador_operar.despesa_acessoria.contrato.mes_referencia_sucesso'),
        });
      },
      error: (err) => {
        this.salvandoMesReferencia = false;
        // Reverte o dropdown e mostra a mensagem do backend (403/404).
        this.contratoMesReferencia = this.contrato?.mesReferencia ?? 'MES_ATUAL';
        this.messageService.add({
          severity: 'error',
          summary:
            err?.error?.message ||
            this.translate.instant('contador_operar.despesa_acessoria.contrato.mes_referencia_erro'),
        });
      },
    });
  }

  // ===========================================================================
  // Cancelar contrato (diálogo com data efetiva)
  // ===========================================================================

  /**
   * True quando a data de cancelamento escolhida é FUTURA (posterior a hoje). O
   * cancelamento do contrato é sempre imediato; a data futura só adia o corte das
   * cobranças, então a UI mostra um aviso para deixar isso explícito.
   */
  get cancelamentoFuturo(): boolean {
    if (!this.cancelarContratoData) {
      return false;
    }
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const escolhida = new Date(this.cancelarContratoData);
    escolhida.setHours(0, 0, 0, 0);
    return escolhida.getTime() > hoje.getTime();
  }

  /** Abre o diálogo de cancelamento com a data pré-preenchida em hoje. */
  abrirCancelarContrato(): void {
    if (this.contratoId == null) {
      return;
    }
    this.cancelarContratoData = new Date();
    this.cancelarContratoDialogVisivel = true;
  }

  /** Card emitiu novo VALOR MENSAL (blur): abre a confirmação antes de aplicar. */
  onValorMensalChange(novoValor: number): void {
    if (this.contratoId == null) return;
    this.edicaoCampo = 'valor';
    this.edicaoValorPendente = novoValor;
    this.confirmarEdicaoVisivel = true;
  }

  /** Card emitiu novo DIA DE COBRANÇA (blur): abre a confirmação antes de aplicar. */
  onDiaCobrancaChange(novoDia: number): void {
    if (this.contratoId == null) return;
    this.edicaoCampo = 'dia';
    this.edicaoValorPendente = novoDia;
    this.confirmarEdicaoVisivel = true;
  }

  /** Card emitiu novo PARCELAS POR MÊS: abre a confirmação antes de aplicar. */
  onParcelasPorMesChange(novo: number): void {
    if (this.contratoId == null) return;
    this.edicaoCampo = 'parcelasPorMes';
    this.edicaoValorPendente = novo;
    this.confirmarEdicaoVisivel = true;
  }

  /** Flag de loading do salvamento (valor / dia / parcelas por mês). */
  get salvandoEdicaoContrato(): boolean {
    return this.salvandoValorMensal || this.salvandoDiaCobranca || this.salvandoParcelasPorMes;
  }

  /** Cancela a edição: fecha o diálogo e reverte o input do card ao valor atual. */
  cancelarEdicaoContrato(): void {
    this.confirmarEdicaoVisivel = false;
    this.edicaoCampo = null;
    this.edicaoValorPendente = null;
    // Nova referência do mesmo contrato -> o card (ngOnChanges) reverte o input.
    this.contrato = this.contrato ? { ...this.contrato } : null;
  }

  /**
   * Confirma a edição: chama o endpoint do campo escolhido. Valor/dia propagam
   * para as parcelas/títulos EM ABERTO sem pagamento parcial (recarrega as
   * parcelas); parcelas por mês só vale para as próximas geradas.
   */
  confirmarEdicaoContrato(): void {
    if (this.contratoId == null || this.edicaoCampo == null || this.edicaoValorPendente == null) {
      return;
    }
    const campo = this.edicaoCampo;
    const valor = this.edicaoValorPendente;
    const contratoId = this.contratoId;

    let chamada$;
    let sucessoKey: string;
    if (campo === 'valor') {
      chamada$ = this.contadorService.alterarValorMensalContrato(contratoId, valor);
      sucessoKey = 'contador_operar.despesa_acessoria.editar_contrato.valor_sucesso';
      this.salvandoValorMensal = true;
    } else if (campo === 'dia') {
      chamada$ = this.contadorService.alterarDiaCobrancaContrato(contratoId, valor);
      sucessoKey = 'contador_operar.despesa_acessoria.editar_contrato.dia_sucesso';
      this.salvandoDiaCobranca = true;
    } else {
      chamada$ = this.contadorService.alterarParcelasPorMesContrato(contratoId, valor);
      sucessoKey = 'contador_operar.despesa_acessoria.editar_contrato.parcelas_por_mes_sucesso';
      this.salvandoParcelasPorMes = true;
    }

    const recalculaParcelas = campo === 'valor' || campo === 'dia';

    chamada$.subscribe({
      next: (contratoAtualizado) => {
        this.resetarEstadoEdicaoContrato();
        this.aplicarContrato(contratoAtualizado);
        // Parcelas por mês NÃO altera as parcelas existentes -> sem recarga.
        if (recalculaParcelas) {
          this.carregarParcelas(0);
        }
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(sucessoKey),
        });
      },
      error: (err) => {
        this.resetarEstadoEdicaoContrato();
        // Recarrega o contrato para reverter o input do card e mostra o erro do backend.
        this.carregarContrato();
        this.messageService.add({
          severity: 'error',
          summary:
            err?.error?.message ||
            this.translate.instant('contador_operar.despesa_acessoria.editar_contrato.erro'),
        });
      },
    });
  }

  /** Zera o estado do diálogo/loadings de edição do contrato. */
  private resetarEstadoEdicaoContrato(): void {
    this.salvandoValorMensal = false;
    this.salvandoDiaCobranca = false;
    this.salvandoParcelasPorMes = false;
    this.confirmarEdicaoVisivel = false;
    this.edicaoCampo = null;
    this.edicaoValorPendente = null;
  }

  /** Fecha o diálogo (sem cancelar), a menos que a requisição esteja em curso. */
  fecharCancelarContrato(): void {
    if (this.cancelandoContrato) {
      return;
    }
    this.cancelarContratoDialogVisivel = false;
  }

  /**
   * Confirma o cancelamento: envia a data escolhida (ISO yyyy-MM-dd) como corte.
   * Em caso de sucesso, recarrega o contrato (que deixa de ser ATIVO e some do
   * card) e avisa; erro (403/404/422) mostra a mensagem do backend.
   */
  confirmarCancelarContrato(): void {
    if (this.contratoId == null) {
      return;
    }
    this.cancelandoContrato = true;
    const dataIso = this.cancelarContratoData
      ? this.dataParaIso(this.cancelarContratoData)
      : undefined;

    this.contadorService.cancelarContrato(this.contratoId, dataIso).subscribe({
      next: () => {
        this.cancelandoContrato = false;
        this.cancelarContratoDialogVisivel = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('contador_operar.despesa_acessoria.cancelar_contrato.sucesso'),
        });
        // O contrato deixa de ser ATIVO: recarrega (o card cai no estado "sem contrato").
        this.carregarContrato();
      },
      error: (err) => {
        this.cancelandoContrato = false;
        this.messageService.add({
          severity: 'error',
          summary:
            err?.error?.message ||
            this.translate.instant('contador_operar.despesa_acessoria.cancelar_contrato.erro'),
        });
      },
    });
  }

  /** Converte um Date (local) para ISO `yyyy-MM-dd`, sem componente de fuso. */
  private dataParaIso(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  // ===========================================================================
  // Tipos de despesa (dropdown)
  // ===========================================================================

  private carregarTiposDespesa(): void {
    this.tipoDespesaService.get<any>('/enums/tipoDespesa', { size: 200, sort: 'nome,asc' }).subscribe({
      next: (res: any) => {
        const tipos: TipoDespesaDTO[] = Array.isArray(res) ? res : res?.content || [];
        this.tipoDespesaOptions = tipos.filter(
          (t) => !!t.enum && !TIPOS_DESPESA_NAO_EXIBIR.includes(t.enum as TipoDeDespesa),
        );
        // O mapa e de ROTULO por enum, nao a lista do dropdown: por isso nasce da
        // resposta completa, nao da filtrada. Hoje as duas sao iguais (o backend ja
        // omite os tipos com `exibir = false`, os mesmos de TIPOS_DESPESA_NAO_EXIBIR);
        // se o backend deixar de filtrar, o rotulo desses tipos continua resolvendo.
        this.tipoDespesaMap = new Map(tipos.map((t) => [t.enum, t.nome]));
        this.buildDespesasTipoOptions();
      },
      error: () => {
        this.tipoDespesaOptions = [];
        // Sem o clear o mapa ficaria preso no idioma anterior e continuaria
        // vencendo o `nome` do payload em tipoDespesaNome().
        this.tipoDespesaMap.clear();
        this.buildDespesasTipoOptions();
      },
    });
  }

  /**
   * Busca na Configuração Geral o tipo de despesa padrão para "gasto especial".
   * O endpoint retorna uma página; usamos o primeiro (e único) registro global.
   * Guarda apenas o enum (string) para pré-selecionar o dropdown depois.
   */
  private carregarTipoDespesaEspecialPadrao(): void {
    this.apiService.read<any>('/configuracaoGeral').subscribe({
      next: (res: any) => {
        const cfg = res?.content?.[0];
        // O backend serializa TipoDespesa como objeto LocalizableEnum
        // ({ enum, nome, ... }); o dropdown usa o enum (string). Extrai o enum,
        // aceitando string por legado/robustez.
        const padrao = cfg?.tipoDespesaEspecialPadrao;
        this.tipoDespesaEspecialPadrao =
          typeof padrao === 'object' ? padrao?.enum ?? null : padrao ?? null;
      },
      error: () => {
        this.tipoDespesaEspecialPadrao = null;
      },
    });
  }

  // ===========================================================================
  // Forma de pagamento (conta do contador)
  // ===========================================================================

  /**
   * Carrega as contas do contador para o dropdown de forma de pagamento.
   * O adiantamento é sempre em Guaraní, então exibimos apenas contas em GUARANI.
   */
  private carregarFormasPagamento(): void {
    // Contas da PRÓPRIA empresa do contador (opt-out do contexto de cliente): sem
    // isso, o ContadorContextoInterceptor injetaria X-Empresa-Contexto e o backend
    // devolveria as contas do CLIENTE em vez das do contador.
    this.contaService.listarContasDaPropriaEmpresa(500).subscribe({
      next: (res: any) => {
        const contas: ContaDTO[] = Array.isArray(res) ? res : res?.content || [];
        this.formaPagamentoOptions = contas
          .filter((c) => this.moedaDaConta(c) === Moeda.GUARANI)
          .map((c) => ({
            label: c.tituloDaConta || c.descricao || `#${c.id}`,
            value: c.id as number,
          }));
      },
      error: () => {
        this.formaPagamentoOptions = [];
      },
    });
  }

  /** Extrai o enum da moeda da conta (pode vir como objeto ou string). */
  private moedaDaConta(conta: ContaDTO): Moeda {
    const moeda = conta.moeda as Moeda | MoedaBackend;
    return typeof moeda === 'string' ? moeda : moeda?.enum;
  }

  /** Rótulo da conta selecionada como forma de pagamento (para exibir na lista). */
  formaPagamentoNome(contaId: number | null): string {
    if (contaId == null) {
      return '-';
    }
    const opcao = this.formaPagamentoOptions.find((o) => o.value === contaId);
    return opcao ? opcao.label : `#${contaId}`;
  }

  /**
   * Rótulo localizado do tipo de despesa.
   *
   * O backend serializa TipoDespesa como objeto LocalizableEnum ({ enum, nome, ... }),
   * com `nome` já traduzido pelo Accept-Language. O mapa vem na FRENTE do `nome`
   * porque ele é recarregado na troca de idioma, enquanto o `nome` do payload
   * ficou no idioma em que a lista foi buscada; sem isso a coluna só mudaria
   * de idioma depois de recarregar a listagem.
   * Mantém suporte a string (enum cru) por robustez/legado, caindo no mapa/i18n.
   */
  tipoDespesaNome(tipo: TipoDespesaBackendDTO | string | null | undefined): string {
    if (tipo == null) {
      return '-';
    }
    if (typeof tipo === 'object') {
      return this.tipoDespesaMap.get(tipo.enum) || tipo.nome || tipo.enum;
    }
    const nome = this.tipoDespesaMap.get(tipo);
    if (nome) {
      return nome;
    }
    const chave = `enums.tipo_despesa.${tipo}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? tipo : traduzido;
  }

  // ===========================================================================
  // Despesas — listagem
  // ===========================================================================

  /**
   * Carrega a PAGINA informada das despesas do contrato (paginacao e ordenacao
   * server-side). O `sort` cai no default do backend quando nao ha ordenacao de
   * cabecalho; `first` reflete o offset da pagina para o p-paginator.
   */
  carregarDespesas(page: number = 0): void {
    if (this.contratoId == null) {
      return;
    }
    this.carregandoDespesas = true;
    this.erroDespesas = false;

    this.despesaService
      .listar(this.contratoId, page, this.rows, this.montarSort(), this.montarFiltrosDespesas())
      .subscribe({
        next: (res) => {
          this.despesas = res.content || [];
          this.totalRecords = res.totalElements ?? 0;
          this.first = page * this.rows;
          this.carregandoDespesas = false;
        },
        error: () => {
          this.despesas = [];
          this.totalRecords = 0;
          this.erroDespesas = true;
          this.carregandoDespesas = false;
        },
      });
  }

  /** Monta os filtros (periodo, tipo, situacao e cobranca) para o GET de gastos. */
  private montarFiltrosDespesas(): DespesaAcessoriaFiltroDTO {
    return {
      de: this.despesasDe,
      ate: this.despesasAte,
      tipoDespesa: this.despesasTipo ?? undefined,
      status: this.despesasStatus ?? undefined,
      cobranca: this.despesasCobranca ?? undefined,
    };
  }

  /**
   * Aplica o filtro de periodo (app-periodo-filtro) sobre os gastos. Converte as
   * datas emitidas para ISO `yyyy-MM-dd` e volta para a primeira pagina.
   */
  onDespesasPeriodoChanged(periodo: PeriodoFiltro): void {
    this.despesasDe = this.dataFiltroParaIso(periodo?.dataInicio);
    this.despesasAte = this.dataFiltroParaIso(periodo?.dataFim);
    this.first = 0;
    this.carregarDespesas(0);
  }

  /** Aplica os filtros de tipo/situacao/cobranca (dropdowns) e recarrega. */
  onDespesasFiltroChange(): void {
    this.first = 0;
    this.carregarDespesas(0);
  }

  /**
   * Monta o `sort` no formato Spring (`campo,asc|desc`). Sem ordenacao de
   * cabecalho retorna o default do backend (`dataPagamento,desc`), mantendo a
   * ordem coerente com o indicador visual da coluna.
   */
  private montarSort(): string {
    if (!this.sortField || this.sortOrder === 0) {
      return 'dataPagamento,desc';
    }
    return `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /**
   * Ordenacao por cabecalho (server-side). A p-table usa [customSort] (nao
   * reordena em memoria — os dados vem paginados do backend) e apenas emite
   * (onSort) com campo/direcao ja resolvidos. Volta para a primeira pagina e
   * recarrega. O guard de "mudanca real" evita loop de requests: numa p-table
   * NAO-lazy com [customSort] o PrimeNG re-emite (onSort) com o MESMO campo toda
   * vez que [value] muda (nossa recarga troca `despesas`).
   */
  onSort(event: { field?: string | string[]; order?: number }): void {
    const campo = typeof event.field === 'string' ? event.field : null;
    const ordem = campo ? (event.order ?? 0) : 0;
    if (campo === this.sortField && ordem === this.sortOrder) {
      return;
    }
    this.sortField = campo;
    this.sortOrder = ordem;
    this.first = 0;
    this.carregarDespesas(0);
  }

  /** Troca de pagina pelo paginador do rodape (recarrega a pagina do backend). */
  onPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this.carregarDespesas(page);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega. */
  onRowsPerPageChange(): void {
    this.first = 0;
    this.carregarDespesas(0);
  }

  /** Cor (severity do p-tag) conforme o status da despesa. */
  statusSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    switch (status) {
      case 'PENDENTE':
        return 'warning';
      case 'ATIVA':
        return 'success';
      case 'RECUSADA':
        return 'danger';
      default: // CANCELADA
        return 'info';
    }
  }

  /** Rótulo localizado do status da despesa. */
  statusLabel(status: string): string {
    const chave = `contador_operar.despesa_acessoria.status.${status.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? status : traduzido;
  }

  /** Só é possível editar/excluir enquanto a despesa está PENDENTE. */
  podeAlterar(despesa: DespesaAcessoriaConsultaDTO): boolean {
    return despesa.status === 'PENDENTE' || this.podeAlterarAtiva(despesa);
  }

  /**
   * Despesa ATIVA ainda é alterável enquanto não foi paga — único caminho de correção em contrato
   * com AUTONOMIA, onde ela nasce ATIVA e nunca passa por PENDENTE.
   *
   * Checagem só para não oferecer um botão que vai falhar: `pagoEm` reflete apenas o título a
   * receber. Quem decide é o backend, que olha os DOIS títulos (422 `despesaacessoria_titulo_pago`).
   */
  podeAlterarAtiva(despesa: DespesaAcessoriaConsultaDTO): boolean {
    return despesa.status === 'ATIVA' && !despesa.pagoEm;
  }

  // ===========================================================================
  // Parcelas do contrato — listagem (somente leitura)
  // ===========================================================================

  /**
   * Carrega a PAGINA informada das parcelas do contrato (paginacao e ordenacao
   * server-side). O `sort` cai no default do backend (`numeroParcela,asc`)
   * quando nao ha ordenacao de cabecalho; `parcelasFirst` reflete o offset para
   * o p-paginator.
   */
  carregarParcelas(page: number = 0): void {
    if (this.contratoId == null) {
      return;
    }
    this.carregandoParcelas = true;
    this.erroParcelas = false;
    // Selecao e por pagina: recarga (pagina/sort/cliente) limpa a selecao.
    this.parcelasSelecionadas = [];

    this.contadorService
      .listarParcelas(this.contratoId, page, this.parcelasRows, this.montarSortParcelas(), this.montarFiltrosParcelas())
      .subscribe({
        next: (res) => {
          this.parcelas = res.content || [];
          this.parcelasTotalRecords = res.totalElements ?? 0;
          this.parcelasFirst = page * this.parcelasRows;
          this.carregandoParcelas = false;
        },
        error: () => {
          this.parcelas = [];
          this.parcelasTotalRecords = 0;
          this.erroParcelas = true;
          this.carregandoParcelas = false;
        },
      });
  }

  /**
   * Busca o TOTAL de parcelas do contrato (todas, SEM filtro de situacao/periodo)
   * para o denominador da coluna "Nº". Chamada leve: `page=0, size=1` — so o
   * `totalElements` interessa. Independente da paginacao/filtros da tabela.
   */
  private carregarTotalParcelasContrato(): void {
    if (this.contratoId == null) {
      return;
    }
    this.contadorService.listarParcelas(this.contratoId, 0, 1).subscribe({
      next: (res) => {
        this.contratoTotalParcelas = res.totalElements ?? null;
      },
      error: () => {
        // Falha aqui nao quebra a tabela: a coluna cai no fallback (so o numero).
        this.contratoTotalParcelas = null;
      },
    });
  }

  /** Monta os filtros de periodo (de/ate) e situacao para o GET de parcelas. */
  private montarFiltrosParcelas(): ParcelaContratoFiltroDTO {
    return {
      de: this.parcelasDe,
      ate: this.parcelasAte,
      situacao: this.parcelasSituacao ?? undefined,
    };
  }

  /**
   * Aplica o filtro de periodo (app-periodo-filtro) sobre as parcelas. Converte
   * as datas `dd/MM/yyyyTHH:mm` do componente para `yyyy-MM-dd` (esperado pelo
   * backend) e recarrega da primeira pagina. `showAll` => sem intervalo.
   */
  onParcelasPeriodoChanged(periodo: PeriodoFiltro): void {
    this.parcelasDe = this.dataFiltroParaIso(periodo?.dataInicio);
    this.parcelasAte = this.dataFiltroParaIso(periodo?.dataFim);
    this.parcelasFirst = 0;
    this.carregarParcelas(0);
  }

  /** Aplica o filtro de situacao (estado do contador) e recarrega. */
  onParcelasSituacaoChange(): void {
    this.parcelasFirst = 0;
    this.carregarParcelas(0);
  }

  /**
   * Recebe a nova situacao emitida pelo grid compartilhado (app-parcelas-contrato,
   * tipado como string generica) e aplica o filtro. O cast para
   * `EstadoContadorParcela` e seguro: as opcoes passadas ao grid nesta tela sao do
   * estado do CONTADOR.
   */
  onParcelasSituacaoChangeValor(valor: string | null): void {
    this.parcelasSituacao = (valor as EstadoContadorParcela | null) ?? null;
    this.onParcelasSituacaoChange();
  }

  /**
   * Converte a data emitida pelo app-periodo-filtro (`dd/MM/yyyyTHH:mm`) para o
   * formato `yyyy-MM-dd` esperado pelo backend. Vazio/indefinido => undefined.
   */
  private dataFiltroParaIso(valor: string | undefined | null): string | undefined {
    if (!valor) {
      return undefined;
    }
    // Ex.: "05/07/2026T00:00" -> pega a parte da data e reordena.
    const parteData = valor.split('T')[0];
    const partes = parteData.split('/');
    if (partes.length !== 3) {
      return undefined;
    }
    const [dia, mes, ano] = partes;
    return `${ano}-${mes}-${dia}`;
  }

  /**
   * Monta o `sort` das parcelas no formato Spring (`campo,asc|desc`). Sem
   * ordenacao de cabecalho retorna o default do backend (`numeroParcela,asc`),
   * mantendo a ordem coerente com o indicador visual da coluna.
   */
  private montarSortParcelas(): string {
    if (!this.parcelasSortField || this.parcelasSortOrder === 0) {
      return 'numeroParcela,asc';
    }
    return `${this.parcelasSortField},${this.parcelasSortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /**
   * Ordenacao por cabecalho das parcelas (server-side). Mesmo padrao do grid de
   * despesas: p-table com [customSort], guard de "mudanca real" para evitar loop
   * de requests quando o PrimeNG reemite (onSort) apos a recarga trocar [value].
   */
  onParcelasSort(event: { field?: string | string[]; order?: number }): void {
    const campo = typeof event.field === 'string' ? event.field : null;
    const ordem = campo ? (event.order ?? 0) : 0;
    if (campo === this.parcelasSortField && ordem === this.parcelasSortOrder) {
      return;
    }
    this.parcelasSortField = campo;
    this.parcelasSortOrder = ordem;
    this.parcelasFirst = 0;
    this.carregarParcelas(0);
  }

  /** Troca de pagina pelo paginador do rodape (recarrega a pagina do backend). */
  onParcelasPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.parcelasRows;
    const page = event.page ?? 0;
    this.parcelasRows = size;
    this.carregarParcelas(page);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega. */
  onParcelasRowsPerPageChange(): void {
    this.parcelasFirst = 0;
    this.carregarParcelas(0);
  }

  // ===========================================================================
  // Excluir parcelas em lote (acao destrutiva com confirmacao)
  // ===========================================================================

  /**
   * Abre o dialog destrutivo (padrao dialog-wave) antes de cancelar em lote as
   * parcelas selecionadas. So dispara a chamada ao confirmar.
   */
  excluirParcelasLote(): void {
    if (this.excluindoParcelas || !this.parcelasExcluiveis) {
      return;
    }
    this.excluirParcelasDialogVisivel = true;
  }

  /** Fecha o dialog "Excluir parcelas" (Cancelar / onHide). */
  fecharExcluirParcelas(): void {
    if (this.excluindoParcelas) {
      return;
    }
    this.excluirParcelasDialogVisivel = false;
  }

  /** Confirma a exclusao em lote a partir do dialog dialog-wave. */
  confirmarExcluirParcelasLote(): void {
    this.executarExcluirParcelasLote();
  }

  private executarExcluirParcelasLote(): void {
    if (this.excluindoParcelas || !this.parcelasExcluiveis) {
      return;
    }
    this.excluindoParcelas = true;

    const selecionadas = this.parcelasSelecionadas.filter((p) => this.parcelaExcluivel(p));
    const ids = selecionadas.map((p) => p.id);
    // Guarda id -> "Parcela N" ANTES de recarregar (o next limpa a selecao).
    const rotulosPorId = new Map(
      selecionadas.map((p) => [
        p.id,
        this.translate.instant('contador_operar.despesa_acessoria.parcelas.rotulo_parcela', {
          numero: p.numeroParcela,
        }),
      ]),
    );

    this.contadorService.cancelarParcelasEmLote(ids).subscribe({
      next: (res) => {
        this.excluindoParcelas = false;
        this.excluirParcelasDialogVisivel = false;
        this.notificarResultadoExclusaoLote(res, rotulosPorId);
        // Recarrega a pagina atual (limpa a selecao dentro de carregarParcelas).
        this.carregarParcelas(this.parcelasPaginaAtual());
      },
      error: (err) => {
        this.excluindoParcelas = false;
        this.excluirParcelasDialogVisivel = false;
        this.tratarErroParcelaLote(err);
      },
    });
  }

  /**
   * Toast de resumo do lote: sucesso quando todas foram canceladas; alerta (warn)
   * com o detalhe dos motivos quando houve parcelas ignoradas.
   */
  private notificarResultadoExclusaoLote(
    res: CancelamentoParcelasLoteResultadoDTO,
    rotulosPorId: Map<number, string>,
  ): void {
    if (res.totalIgnoradas > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.despesa_acessoria.parcelas.excluir_parcial', {
          canceladas: res.totalCanceladas,
          ignoradas: res.totalIgnoradas,
        }),
        detail: this.montarDetalheIgnoradas(res.ignoradas, rotulosPorId),
        life: 8000,
      });
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('contador_operar.despesa_acessoria.parcelas.excluir_sucesso', {
        count: res.totalCanceladas,
      }),
    });
  }

  /** Detalhe amigavel do lote parcial: uma linha por item ignorado (rotulo + motivo). */
  private montarDetalheIgnoradas(
    ignoradas: ParcelaLoteIgnoradaDTO[],
    rotulosPorId: Map<number, string>,
  ): string {
    return (ignoradas || [])
      .map((ig) => `${rotulosPorId.get(ig.id) || `#${ig.id}`}: ${ig.motivo}`)
      .join('\n');
  }

  /** Erro do cancelamento em lote: usa a mensagem do backend (400/403) quando houver. */
  private tratarErroParcelaLote(err: { status?: number; error?: { message?: string } }): void {
    const summary =
      err?.error?.message ||
      this.translate.instant('contador_operar.despesa_acessoria.parcelas.excluir_erro');
    this.messageService.add({ severity: 'error', summary });
  }

  /** Indice 0-based da pagina atual das parcelas (offset / itens por pagina). */
  private parcelasPaginaAtual(): number {
    return this.parcelasRows > 0 ? Math.floor(this.parcelasFirst / this.parcelasRows) : 0;
  }

  /**
   * Abre a fatura da parcela em NOVA aba, obrigatoriamente: a rota
   * /notas-list/fatura preserva o contexto de operacao (ROTAS_CONTEXTO) e o
   * interceptor injetaria X-Empresa-Contexto no GET /nota/{id}, buscando a nota
   * na empresa do cliente operado — mas a venda consolidada pertence a empresa
   * do CONTADOR. Aba nova = instancia sem contexto = nota resolvida na empresa
   * certa, e a aba atual preserva cliente selecionado e paginacao.
   */
  abrirNota(parcela: ParcelaContratoConsultaDTO): void {
    if (parcela.notaContadorId == null) {
      return;
    }
    window.open(`/notas-list/fatura/${parcela.notaContadorId}`, '_blank');
  }

  // ===========================================================================
  // Adicionar parcelas (dialog: quantidade + periodicidade)
  // ===========================================================================

  /**
   * Abre o dialog de adicionar parcelas. A "parcelas por mês" já vem preenchida
   * com o valor definido no contrato (`parcelasPorMes`), mas o contador pode
   * alterá-la; a data do 1º vencimento começa vazia (comportamento default).
   */
  abrirAdicionarParcelas(): void {
    this.novaQuantidadeParcelas = 1;
    this.novaPeriodicidadeParcelas = this.contrato?.parcelasPorMes ?? 1;
    this.novaDataPrimeiroVencimento = null;
    this.adicionarParcelasSubmetido = false;
    this.adicionarParcelasDialogVisivel = true;
  }

  fecharAdicionarParcelas(): void {
    this.adicionarParcelasDialogVisivel = false;
  }

  /** Validacao client-side: quantidade e periodicidade devem ser inteiros >= 1. */
  get adicionarParcelasValido(): boolean {
    const q = this.novaQuantidadeParcelas;
    const p = this.novaPeriodicidadeParcelas;
    return q != null && q >= 1 && p != null && p >= 1;
  }

  confirmarAdicionarParcelas(): void {
    this.adicionarParcelasSubmetido = true;
    if (this.adicionandoParcelas || !this.adicionarParcelasValido || this.contratoId == null) {
      return;
    }
    this.adicionandoParcelas = true;

    // So envia dataPrimeiroVencimento quando o usuario escolheu uma data
    // (vazio => backend mantem o comportamento default). Converte sem shift de fuso.
    const body: AdicionarParcelasDTO = {
      quantidade: this.novaQuantidadeParcelas as number,
      periodicidade: this.novaPeriodicidadeParcelas as number,
    };
    if (this.novaDataPrimeiroVencimento) {
      body.dataPrimeiroVencimento = this.toIsoDate(this.novaDataPrimeiroVencimento);
    }

    this.contadorService
      .adicionarParcelas(this.contratoId, body)
      .subscribe({
        next: (contratoAtualizado) => {
          this.adicionandoParcelas = false;
          this.adicionarParcelasDialogVisivel = false;
          // Atualiza o card do contrato (nova dataVencimento) e recarrega parcelas.
          this.aplicarContrato(contratoAtualizado);
          this.carregarParcelas(0);
          // Adicionar aumenta o total do contrato -> recarrega o denominador da coluna "Nº".
          this.carregarTotalParcelasContrato();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('contador_operar.despesa_acessoria.parcelas.adicionar_sucesso'),
          });
        },
        error: (err) => {
          this.adicionandoParcelas = false;
          const summary =
            err?.error?.message ||
            this.translate.instant('contador_operar.despesa_acessoria.parcelas.adicionar_erro');
          this.messageService.add({ severity: 'error', summary });
        },
      });
  }

  // ===========================================================================
  // Faturar parcelas (preparar -> criar a venda na empresa do contador -> vincular)
  // ===========================================================================

  /**
   * Passo 1 do faturar: chama o "preparar" com as parcelas selecionadas. Ao
   * receber os dados, abre o dialog de confirmacao (forma de pagamento). Erros
   * (404/422) exibem a mensagem amigavel do backend.
   */
  faturarParcelas(): void {
    if (this.processandoFaturamento || !this.parcelasFaturaveis) {
      return;
    }
    const ids = this.parcelasSelecionadas.map((p) => p.id);
    // Resiliencia: se o vendedor nao carregou no ngOnInit (falha de rede), tenta
    // de novo agora para que o dialog ja abra com o nome preenchido.
    if (!this.vendedorPessoaId) {
      this.carregarVendedor();
    }
    this.processandoFaturamento = true;

    this.contadorService.prepararFaturamento(ids).subscribe({
      next: (prep) => {
        this.processandoFaturamento = false;
        this.prepFaturamento = prep;
        this.formaPagamentoFaturar = 'prazo';
        // Sempre reinicia o juros ao abrir (desmarcado, sem percentual).
        this.adicionarJuros = false;
        this.percentualJuros = null;
        this.faturarDialogVisivel = true;
      },
      error: (err) => {
        this.processandoFaturamento = false;
        this.tratarErroFaturamento(err);
      },
    });
  }

  fecharFaturar(): void {
    this.faturarDialogVisivel = false;
    this.prepFaturamento = null;
    this.adicionarJuros = false;
    this.percentualJuros = null;
  }

  /** Marcou/desmarcou "Adicionar juros": limpa o percentual ao desmarcar e recalcula o preview. */
  onToggleJuros(): void {
    if (!this.adicionarJuros) {
      this.percentualJuros = null;
    }
    this.recalcularFaturamentoComJuros();
  }

  /** Alterou o percentual: recalcula o preview do juros (chamado no blur do input). */
  onPercentualJurosChange(): void {
    this.recalcularFaturamentoComJuros();
  }

  /**
   * Re-chama o "preparar" com o percentual atual para atualizar o preview do juros
   * (valor e total). O backend calcula o juros das parcelas ATRASADAS; o front nao
   * replica a regra. Idempotente (find-or-create de pessoa/item).
   */
  private recalcularFaturamentoComJuros(): void {
    if (!this.prepFaturamento || this.processandoFaturamento) {
      return;
    }
    const ids = this.parcelasSelecionadas.map((p) => p.id);
    const percentual = this.adicionarJuros ? this.percentualJuros : null;
    this.recalculandoJuros = true;
    this.contadorService.prepararFaturamento(ids, percentual).subscribe({
      next: (prep) => {
        this.recalculandoJuros = false;
        this.prepFaturamento = prep;
      },
      error: (err) => {
        this.recalculandoJuros = false;
        this.tratarErroFaturamento(err);
      },
    });
  }

  /**
   * Carrega o vendedor da venda: a PESSOA do usuario logado (o contador).
   *
   * `GET /usuario/{id}` NAO esta na allowlist do `ContadorContextoInterceptor`
   * (nem nas rotas exatas nem nos prefixos), logo a request NUNCA recebe o
   * header `X-Empresa-Contexto` — o usuario retornado e o do contador, na
   * empresa do contador, que e exatamente o dono da nota criada pelo
   * `postParaVendaSemContextoContador`. Por isso nao e necessario o opt-out
   * explicito aqui. Falha e silenciosa: o guard do faturar avisa o usuario.
   */
  private carregarVendedor(): void {
    const usuarioId = sessionStorage.getItem('userId');
    if (!usuarioId) {
      return;
    }
    this.usuarioService.getById(`/usuario/${usuarioId}`).subscribe({
      next: (res) => {
        this.vendedorPessoaId = res?.pessoa?.id || null;
        this.vendedorNome = res?.pessoa?.nome || res?.username || '';
      },
      error: () => {
        this.vendedorPessoaId = null;
        this.vendedorNome = '';
      },
    });
  }

  /**
   * Passo do meio + passo 2: cria a VENDA (POST /nota SEM contexto de cliente —
   * a nota e da empresa do contador) e, com o id retornado, chama o "vincular".
   * Se a nota for criada mas o vincular falhar, avisa claramente (a nota existe).
   */
  async confirmarFaturamento(): Promise<void> {
    if (this.processandoFaturamento || !this.prepFaturamento) {
      return;
    }
    // Guard: o backend exige `vendedor_id` em notas de VENDA. Sem vendedor
    // identificado, nem chega a enviar a requisicao (evita 400 sem contexto).
    if (!this.vendedorPessoaId) {
      this.carregarVendedor();
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant(
          'contador_operar.despesa_acessoria.parcelas.faturar_vendedor_ausente',
        ),
      });
      return;
    }

    const ids = this.parcelasSelecionadas.map((p) => p.id);
    const percentualJuros = this.adicionarJuros ? this.percentualJuros : null;
    this.processandoFaturamento = true;

    // --- Passo do meio: criar a nota (falha aqui = nada foi vinculado) ---
    let notaId = 0;
    try {
      // Refaz o "preparar" com o percentual atual para garantir que o juros do item
      // da nota case com o que o backend aplicara no lado cliente no vincular.
      const prep = await firstValueFrom(
        this.contadorService.prepararFaturamento(ids, percentualJuros),
      );
      this.prepFaturamento = prep;
      const cotacaoId = await this.buscarCotacaoIdFaturamento();
      const nota = this.montarNotaFaturamento(prep, this.formaPagamentoFaturar, cotacaoId);
      const resp = await firstValueFrom(
        this.notaService.postParaVendaSemContextoContador('/nota', nota),
      );
      notaId = resp?.id || 0;
      if (!notaId) {
        throw new Error('nota_sem_id');
      }
    } catch (err) {
      this.processandoFaturamento = false;
      this.tratarErroFaturamento(err);
      return;
    }

    // --- Passo 2: vincular. A nota JA existe; falha aqui e avisada como parcial ---
    this.contadorService.vincularFaturamento(ids, notaId, percentualJuros).subscribe({
      next: (res) => {
        this.processandoFaturamento = false;
        this.fecharFaturar();
        this.notificarResultadoFaturamento(res);
        this.recarregarAposFaturamento();
      },
      error: (err) => {
        this.processandoFaturamento = false;
        this.fecharFaturar();
        // A nota FOI criada, mas nao vinculou: avisa claramente com o id da nota.
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'contador_operar.despesa_acessoria.parcelas.faturar_vinculo_falhou',
            { notaId },
          ),
          detail: err?.error?.message || undefined,
          life: 10000,
        });
        // Recarrega mesmo assim (a nota pode ter efeitos) e limpa a selecao.
        this.recarregarAposFaturamento();
      },
    });
  }

  /**
   * Cotacao usada no faturamento: a ULTIMA cotacao da empresa do CONTADOR, em
   * QUALQUER moeda. A venda-honorario e sempre em Guarani e toda cotacao ja
   * carrega os cambios (realGuarani, dolarGuarani...), entao basta uma cotacao
   * valida do contador — nao precisa ser a "GUARANI". `/cotacao/ultimaCotacao`
   * NAO esta na allowlist de contexto, entao resolve na empresa do PROPRIO
   * contador (nao no cliente) e nao exige cotacao "de hoje". Isso destrava o
   * faturamento quando o cliente opera em moeda != Guarani (antes a busca por
   * GUARANI caia no contexto do cliente e falhava com "Cotacao nao cadastrada").
   */
  private async buscarCotacaoIdFaturamento(): Promise<number> {
    const cotacao = await firstValueFrom(this.cotacaoService.get('/cotacao/ultimaCotacao'));
    const id = (cotacao as { id?: number })?.id;
    if (!id) {
      throw new Error('cotacao_nao_encontrada');
    }
    return id;
  }

  /**
   * Monta o NotaDTO do faturamento (empresa do contador): VENDA_COMUM, GUARANI,
   * 1 item de servico isento. Contado => recebido em dinheiro (sem titulo). A
   * prazo => 1 titulo com vencimento da 1a parcela. Campos de IVA nao sao
   * enviados (o backend calcula).
   */
  private montarNotaFaturamento(
    prep: PrepararFaturamentoDTO,
    forma: 'contado' | 'prazo',
    cotacaoId: number,
  ): Record<string, unknown> {
    const agora = toLocalISOString();
    const item = {
      item_id: prep.itemServicoId,
      tipoItem: 'SERVICO',
      tipoDeImposto: 'IVA_ISENTO',
      quantidade: 1,
      valorUnitario: prep.valorTotal,
      custoUnitario: 0,
      custoTotal: 0,
      valorTotal: prep.valorTotal,
      desconto: 0,
      valorUnitarioComDesconto: prep.valorTotal,
      valorTotalComDesconto: prep.valorTotal,
      observacao: prep.descricaoServico,
      modificadores: [],
      variantesDePreco: [],
    };

    // Juros das parcelas atrasadas: 1 item "Juros" a mais na nota (quando houver).
    const jurosTotal = prep.jurosTotal || 0;
    const itens: Record<string, unknown>[] = [item];
    if (jurosTotal > 0) {
      itens.push({
        item_id: prep.itemJurosId,
        tipoItem: 'SERVICO',
        tipoDeImposto: 'IVA_ISENTO',
        quantidade: 1,
        valorUnitario: jurosTotal,
        custoUnitario: 0,
        custoTotal: 0,
        valorTotal: jurosTotal,
        desconto: 0,
        valorUnitarioComDesconto: jurosTotal,
        valorTotalComDesconto: jurosTotal,
        observacao: this.translate.instant(
          'contador_operar.despesa_acessoria.parcelas.faturar_juros_observacao',
        ),
        modificadores: [],
        variantesDePreco: [],
      });
    }
    // Total da venda = serviço + juros (soma dos itens).
    const totalVenda = prep.valorTotal + jurosTotal;

    const nota: Record<string, unknown> = {
      emissao: agora,
      emissaoFatura: agora,
      tipoDeTransacao: 'VENDA',
      tipoNota: 'VENDA_COMUM',
      moeda: 'GUARANI',
      cotacao_id: cotacaoId,
      ehFaturaLegal: false,
      // Quirk do front: custoTotalDosItens recebe o subtotal de VENDA (= total dos itens).
      custoTotalDosItens: totalVenda,
      valorTotal: totalVenda,
      valorDoDesconto: 0,
      tipoDeDesconto: 'SEM_DESCONTO',
      porcentagemDesconto: 0,
      valorTotalComDesconto: totalVenda,
      valorTotalEmServicos: totalVenda,
      valorTotalEmMercadorias: 0,
      valorRecebidoNaConta: 0,
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmReal: 0,
      valorRecebidoEmPeso: 0,
      informacaoDaTransacao: '',
      cancelado: false,
      pessoa_id: prep.pessoaId,
      // Obrigatorio para VENDA (NotaUtil.verificarInclusaoVendedor): a pessoa do
      // usuario logado (contador), dono da empresa em que a nota e criada.
      vendedor_id: this.vendedorPessoaId,
      observacao: prep.descricaoServico,
      chaveNotaEletronica: '',
      itemDeNota: itens,
      baixas: [],
      titulos: [] as unknown[],
      numeroParcelas: 0,
    };

    if (forma === 'prazo') {
      nota['condicaoDePagamento'] = 'CREDITO';
      nota['numeroParcelas'] = 1;
      nota['conta_id'] = null;
      nota['titulos'] = [
        {
          emissao: agora,
          vencimento: this.dataIsoParaDateTime(prep.vencimentoPrimeiraParcela),
          valor: totalVenda,
          saldo: totalVenda,
          numeroParcela: 1,
          tipoReceita: 'VENDAS_A_PRAZO',
          tipoOperacaoFinanceira: 'ENTRADA',
          moeda: 'GUARANI',
          pessoa_id: null,
          situacaoDeCobranca: 'ABERTO',
        },
      ];
    } else {
      // A vista (contado): recebido em dinheiro (Guarani), sem titulo, sem conta.
      nota['condicaoDePagamento'] = 'CONTADO';
      nota['numeroParcelas'] = 0;
      nota['titulos'] = [];
      nota['valorRecebidoEmGuarani'] = totalVenda;
    }

    return nota;
  }

  /** Converte `yyyy-MM-dd` em ISO local `yyyy-MM-ddT00:00:00` (sem shift de fuso). */
  private dataIsoParaDateTime(iso: string): string {
    if (!iso) {
      return toLocalISOString();
    }
    const partes = iso.split('-');
    if (partes.length !== 3) {
      return toLocalISOString();
    }
    const [ano, mes, dia] = partes.map(Number);
    return toLocalISOString(new Date(ano, mes - 1, dia));
  }

  /** Toast do vincular: sucesso quando tudo vinculou; alerta com motivos se ha ignoradas. */
  private notificarResultadoFaturamento(res: VincularFaturamentoResultadoDTO): void {
    if (res.ignoradas && res.ignoradas.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.despesa_acessoria.parcelas.faturar_parcial', {
          vinculadas: res.totalVinculadas,
          ignoradas: res.ignoradas.length,
        }),
        detail: res.ignoradas.map((ig) => ig.motivo).join('\n'),
        life: 8000,
      });
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('contador_operar.despesa_acessoria.parcelas.faturar_sucesso', {
        count: res.totalVinculadas,
      }),
    });
  }

  /** Erro do faturamento (preparar/nota): mensagem do backend, cotacao ou generica. */
  private tratarErroFaturamento(err: unknown): void {
    const e = err as { message?: string; error?: { message?: string } };
    let summary: string;
    if (e?.message === 'cotacao_nao_encontrada') {
      summary = this.translate.instant('contador_operar.despesa_acessoria.parcelas.faturar_cotacao_erro');
    } else {
      summary =
        e?.error?.message ||
        this.translate.instant('contador_operar.despesa_acessoria.parcelas.faturar_erro');
    }
    this.messageService.add({ severity: 'error', summary });
  }

  /** Recarrega o card do contrato + as parcelas (limpa a selecao dentro da recarga). */
  private recarregarAposFaturamento(): void {
    this.carregarContrato();
  }

  /**
   * Rótulo da coluna "Nº": `numeroParcela/totalDoContrato` (ex.: "1/8"). O
   * denominador é o total de parcelas do CONTRATO (todas, sem filtro), obtido em
   * `contratoTotalParcelas`. Enquanto esse total não carregou, cai no fallback
   * (só o número) para não exibir "1/undefined".
   */
  numeroParcelaLabel(parcela: ParcelaContratoConsultaDTO): string {
    return this.contratoTotalParcelas != null
      ? `${parcela.numeroParcela}/${this.contratoTotalParcelas}`
      : `${parcela.numeroParcela}`;
  }

  /**
   * Competencia da parcela como `MM/yyyy`, a partir de `dataReferencia` (ISO
   * `yyyy-MM-dd`, 1o dia do mes). Formatado por split de string para evitar o
   * shift de fuso do pipe `date` (que interpretaria o ISO como UTC e poderia
   * cair no mes anterior no fuso do Paraguai).
   */
  competenciaParcela(dataReferencia: string | null | undefined): string {
    if (!dataReferencia) {
      return '-';
    }
    const [ano, mes] = dataReferencia.split('-');
    return ano && mes ? `${mes}/${ano}` : dataReferencia;
  }

  /** Cor (severity do p-tag) da situacao do CLIENTE (lado a pagar). */
  estadoClienteSeverity(estado: EstadoClienteParcela): 'success' | 'warning' | 'danger' {
    switch (estado) {
      case 'PAGA':
        return 'success';
      case 'CANCELADA':
        return 'danger';
      default: // PENDENTE
        return 'warning';
    }
  }

  /** Rotulo localizado da situacao do CLIENTE. */
  estadoClienteLabel(estado: EstadoClienteParcela): string {
    const chave = `contador_operar.despesa_acessoria.parcelas.estado_cliente.${estado.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? estado : traduzido;
  }

  /** Cor (severity do p-tag) da situacao do CONTADOR (lado a receber). */
  estadoContadorSeverity(estado: EstadoContadorParcela): 'success' | 'warning' | 'info' | 'danger' {
    switch (estado) {
      case 'RECEBIDA':
        return 'success';
      case 'FATURADA':
        return 'info';
      case 'CANCELADA':
        return 'danger';
      default: // PENDENTE
        return 'warning';
    }
  }

  /** Rotulo localizado da situacao do CONTADOR. */
  estadoContadorLabel(estado: EstadoContadorParcela): string {
    const chave = `contador_operar.despesa_acessoria.parcelas.estado_contador.${estado.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? estado : traduzido;
  }

  // ===========================================================================
  // Situação de recebimento (tag de pagamento)
  // ===========================================================================

  /**
   * A tag de pagamento (Pago/A receber) só faz sentido para despesas ATIVA e
   * NÃO especiais. A especial não gera título a receber, portanto `pagoEm` fica
   * sempre null e não deve virar "A receber". Nos demais status a coluna Status
   * já cobre a situação.
   */
  mostrarTagPagamento(despesa: DespesaAcessoriaConsultaDTO): boolean {
    return despesa.status === 'ATIVA' && !despesa.especial;
  }

  /** True quando o título a receber vinculado à despesa já foi quitado. */
  private pago(despesa: DespesaAcessoriaConsultaDTO): boolean {
    return despesa.pagoEm != null;
  }

  /**
   * Recebimento PARCIAL: o título a receber continua aberto, mas já entrou parte.
   *
   * O backend manda o saldo do título; menor que o valor do gasto = houve baixa parcial. Sem
   * esta distinção a linha diria "A receber" tanto para quem não pagou nada quanto para quem
   * já pagou quase tudo.
   */
  private parcial(despesa: DespesaAcessoriaConsultaDTO): boolean {
    if (this.pago(despesa)) return false;
    const saldo = despesa.saldoAReceber;
    if (saldo == null) return false;
    return Number(saldo) < Number(despesa.valor ?? 0);
  }

  /** Rótulo localizado da tag de pagamento. */
  pagamentoLabel(despesa: DespesaAcessoriaConsultaDTO): string {
    if (this.pago(despesa)) {
      return this.translate.instant('contador_operar.despesa_acessoria.pagamento.pago');
    }
    return this.translate.instant(
      this.parcial(despesa)
        ? 'contador_operar.despesa_acessoria.pagamento.parcial'
        : 'contador_operar.despesa_acessoria.pagamento.a_receber',
    );
  }

  /** Cor (severity do p-tag) da tag de pagamento. */
  pagamentoSeverity(despesa: DespesaAcessoriaConsultaDTO): 'success' | 'warning' | 'info' {
    if (this.pago(despesa)) return 'success';
    return this.parcial(despesa) ? 'info' : 'warning';
  }

  /** Tooltip da tag: pago mostra a data; parcial mostra quanto ainda falta. */
  pagamentoTooltip(despesa: DespesaAcessoriaConsultaDTO): string {
    if (this.pago(despesa)) {
      if (!despesa.pagoEm) return '';
      return this.translate.instant('contador_operar.despesa_acessoria.pagamento.pago_em', {
        data: this.formatarDataHora(despesa.pagoEm),
      });
    }
    if (!this.parcial(despesa)) return '';
    return this.translate.instant('contador_operar.despesa_acessoria.pagamento.parcial_falta', {
      saldo: this.formatarValorDoGasto(Number(despesa.saldoAReceber ?? 0)),
      total: this.formatarValorDoGasto(Number(despesa.valor ?? 0)),
    });
  }

  /** Valor no formato da moeda do gasto (sempre Guarani nos títulos da despesa). */
  private formatarValorDoGasto(valor: number): string {
    return new Intl.NumberFormat(this.moedaConfig.locale, {
      style: 'currency',
      currency: this.moedaConfig.symbol,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(valor);
  }

  // ===========================================================================
  // Despesas — formulário (adicionar/editar)
  // ===========================================================================

  private formVazio(): DespesaAcessoriaFormDTO {
    return {
      valor: null,
      dataPagamento: null,
      tipoDespesa: null,
      especial: false,
      contaId: null,
      // Default do formulário/backend: competência do mês atual.
      mesReferencia: 'MES_ATUAL',
    };
  }

  private formInvalido(): boolean {
    return (
      this.form.valor == null ||
      this.form.valor <= 0 ||
      !this.form.dataPagamento ||
      !this.form.tipoDespesa ||
      // Forma de pagamento é obrigatória quando NÃO é despesa especial.
      (!this.form.especial && this.form.contaId == null)
    );
  }

  /**
   * Ao marcar como especial: limpa a forma de pagamento (não se aplica) e
   * pré-seleciona o "Tipo de gasto" com o padrão da Configuração Geral
   * (ex.: Retenções de IVA). O dropdown continua editável — o usuário pode trocar.
   */
  onEspecialChange(): void {
    if (this.form.especial) {
      this.form.contaId = null;
      if (this.tipoDespesaEspecialPadrao) {
        this.form.tipoDespesa = this.tipoDespesaEspecialPadrao;
      }
    }
  }

  /** True quando o formulário está em modo edição. */
  get editando(): boolean {
    return this.editandoId != null;
  }

  /** Preenche o formulário com a despesa selecionada e abre a aba do formulário. */
  editar(despesa: DespesaAcessoriaConsultaDTO): void {
    this.editandoId = despesa.id;
    this.editandoAtiva = despesa.status === 'ATIVA';
    this.editandoAtivaSemAjusteDeCaixa =
      this.editandoAtiva && !despesa.ajustaCaixaAutomaticamente;
    this.submetido = false;
    this.form = {
      valor: despesa.valor,
      dataPagamento: this.parseIsoDate(despesa.dataPagamento),
      // O backend retorna tipoDespesa como objeto { enum, nome, ... }; o dropdown
      // usa o enum (string). Extrai o enum, aceitando string por legado.
      tipoDespesa:
        typeof despesa.tipoDespesa === 'object'
          ? despesa.tipoDespesa?.enum ?? null
          : despesa.tipoDespesa,
      especial: despesa.especial,
      contaId: despesa.contaId,
      // Pré-preenche o mês de referência (default MES_ATUAL para itens legados sem o campo).
      mesReferencia: this.extrairMesReferencia(despesa.mesReferencia),
    };
    this.buildAbaOpcoes(); // rotulo da aba passa a "Editar gasto"
    this.abaAtiva = 'form'; // alterna para o formulario ja preenchido
  }

  /** Cancela a edição em andamento: volta o formulário ao estado de "novo gasto". */
  cancelarEdicao(): void {
    this.resetForm();
  }

  /** Abre o formulário em modo "novo gasto" a partir da listagem. */
  novoGasto(): void {
    this.resetForm();
    this.abaAtiva = 'form';
  }

  private resetForm(): void {
    this.form = this.formVazio();
    this.editandoId = null;
    this.editandoAtiva = false;
    this.editandoAtivaSemAjusteDeCaixa = false;
    this.submetido = false;
    this.buildAbaOpcoes(); // rotulo da aba volta a "Novo gasto"
  }

  /** Salva a despesa (cadastro ou edição), convertendo FormDTO -> DTO. */
  submit(): void {
    this.submetido = true;

    if (this.contratoId == null) {
      return;
    }
    if (this.formInvalido()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.despesa_acessoria.campos_obrigatorios'),
      });
      return;
    }

    const body: DespesaAcessoriaDTO = {
      valor: this.form.valor as number,
      dataPagamento: this.toIsoDate(this.form.dataPagamento as Date),
      tipoDespesa: this.form.tipoDespesa as string,
      especial: this.form.especial,
      // Forma de pagamento só é enviada quando NÃO especial.
      contaId: this.form.especial ? null : this.form.contaId,
      mesReferencia: this.form.mesReferencia,
    };

    this.salvando = true;
    const edicao = this.editandoId != null;
    const requisicao = edicao
      ? this.despesaService.editar(this.contratoId, this.editandoId as number, body)
      : this.despesaService.cadastrar(this.contratoId, body);

    requisicao.subscribe({
      next: () => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(
            edicao
              ? 'contador_operar.despesa_acessoria.sucesso_edicao'
              : 'contador_operar.despesa_acessoria.sucesso_cadastro',
          ),
        });
        this.resetForm();
        // Apos salvar, volta para a listagem exibindo o item recem-criado/editado.
        this.abaAtiva = 'lista';
        this.carregarDespesas();
      },
      error: (err) => {
        this.salvando = false;
        this.exibirErroSilenciadoPeloInterceptor(
          err,
          'contador_operar.despesa_acessoria.erro_salvar',
        );
      },
    });
  }

  /**
   * O toast de erro de mutacao ja vem do `HttpErrorInterceptor`, com a mensagem do backend.
   * Exibir tambem aqui duplicava o mesmo texto na tela.
   *
   * Sobra so o que o interceptor silencia de proposito (403/404): sem isto a acao falharia em
   * silencio, porque o `AuthInterceptor` trata apenas 401.
   */
  private exibirErroSilenciadoPeloInterceptor(err: any, chaveFallback: string): void {
    if (err?.status !== 403 && err?.status !== 404) {
      return;
    }
    this.messageService.add({
      severity: 'error',
      summary: err?.error?.message || this.translate.instant(chaveFallback),
    });
  }

  // ===========================================================================
  // Despesas — exclusão (soft)
  // ===========================================================================

  /** Abre o diálogo de confirmação (padrão dialog-wave) para a despesa PENDENTE. */
  excluir(despesa: DespesaAcessoriaConsultaDTO): void {
    this.despesaParaExcluir = despesa;
    this.excluirDialogVisivel = true;
  }

  /** Fecha o diálogo de confirmação de exclusão (Cancelar / onHide). */
  fecharDialogExcluir(): void {
    this.excluirDialogVisivel = false;
    this.despesaParaExcluir = null;
  }

  /** Confirma a exclusão a partir do diálogo customizado. */
  confirmarExclusao(): void {
    if (this.despesaParaExcluir == null || this.contratoId == null) {
      return;
    }
    const despesa = this.despesaParaExcluir;
    this.excluindo = true;
    this.despesaService.excluir(this.contratoId, despesa.id).subscribe({
      next: () => {
        this.excluindo = false;
        this.fecharDialogExcluir();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('contador_operar.despesa_acessoria.sucesso_exclusao'),
        });
        if (this.editandoId === despesa.id) {
          this.resetForm();
        }
        this.carregarDespesas();
      },
      error: (err) => {
        this.excluindo = false;
        this.exibirErroSilenciadoPeloInterceptor(
          err,
          'contador_operar.despesa_acessoria.erro_excluir',
        );
      },
    });
  }

  // ===========================================================================
  // Utilitários de data
  // ===========================================================================

  /** Converte Date -> ISO `yyyy-MM-dd` (data local, sem timezone shift). */
  private toIsoDate(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  /** Formata um ISO LocalDateTime para `dd/MM/yyyy` (para tooltips). */
  private formatarDataHora(iso: string): string {
    const data = new Date(iso);
    if (isNaN(data.getTime())) {
      return iso;
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    return `${dia}/${mes}/${data.getFullYear()}`;
  }

  /** Converte ISO `yyyy-MM-dd` -> Date local (para o p-calendar). */
  private parseIsoDate(iso: string): Date | null {
    if (!iso) {
      return null;
    }
    const [ano, mes, dia] = iso.split('-').map(Number);
    if (!ano || !mes || !dia) {
      return null;
    }
    return new Date(ano, mes - 1, dia);
  }
}
